import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { PROFILE_API, WALLET_API } from "../../data/api";
import axios from "axios";
import {
  User,
  Mail,
  Phone,
  Calendar,
  Edit,
  Wallet,
  Crown,
  Building,
  FileText,
  Globe,
  Users,
  Award,
  ChevronDown,
  ChevronUp,
  Camera,
  Share2,
  MapPin,
  CreditCard,
  Shield,
  Briefcase,
  TrendingUp,
  Star,
  CheckCircle,
  Clock,
  Plus,
  Save,
  X,
} from "lucide-react";
import EditModal from "./EditModal";

// Animated Accordion Component
function AccordionSection({ title, icon: Icon, isOpen, onToggle, children, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
        >
          <ChevronDown size={18} className="text-gray-600" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-5 pt-0 space-y-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Info Row Component
function InfoRow({ icon: Icon, label, value, color = "blue" }) {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color === "green" ? "from-green-500 to-emerald-500" : color === "purple" ? "from-purple-500 to-pink-500" : "from-blue-500 to-cyan-500"} flex items-center justify-center`}>
          <Icon size={18} className="text-white" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="font-medium text-gray-800">{value || "Not Provided"}</p>
        </div>
      </div>
      {value && <CheckCircle size={18} className="text-green-500" />}
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, color, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );
}

const ProfilePage = () => {
  const user = useSelector((state) => state.auth.user);
  const userId = user?.userId;
  const subscription = useSelector((state) => state.subscription);

  const [editSection, setEditSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [profileData, setProfileData] = useState({});
  const [walletBalance, setWalletBalance] = useState(0);
  const [accordionOpen, setAccordionOpen] = useState({
    business: false,
    documents: false,
    extra: false,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!userId) {
          setErrorMessage("User ID not found! Please login again.");
          return;
        }
        const res = await fetch(PROFILE_API.GET_BY_ID(userId));
        if (!res.ok) throw new Error("Failed to load profile");
        const data = await res.json();
        setProfileData(data);
      } catch (err) {
        setErrorMessage(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchWallet = async () => {
      try {
        if (!userId) return;
        const res = await axios.get(WALLET_API.GET_BALANCE(userId));
        setWalletBalance(res.data.balance || 0);
      } catch (err) {
        console.error("Wallet fetch error:", err);
      }
    };

    fetchProfile();
    fetchWallet();
  }, [userId]);

  const toggleAccordion = (section) => {
    setAccordionOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1 }}
          className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <X size={40} className="text-red-500" />
          </div>
          <p className="text-red-600 text-lg font-medium">{errorMessage}</p>
        </div>
      </div>
    );
  }

  const memberSince = profileData.createdAt?.split("T")[0];
  const getSubscriptionColor = () => {
    switch (subscription.plan) {
      case "Pro": return "from-purple-500 to-pink-500";
      case "Enterprise": return "from-orange-500 to-red-500";
      default: return "from-gray-400 to-gray-500";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 rounded-3xl p-8 text-white shadow-2xl overflow-hidden"
        >
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="absolute top-1/2 right-20 w-32 h-32 bg-white/10 rounded-full" />

          <div className="relative flex flex-col md:flex-row items-center gap-8">
            {/* Profile Image */}
            <div className="relative">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="w-32 h-32 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white/30"
              >
                {profileData.profileImage ? (
                  <img
                    src={profileData.profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <User size={48} className="text-white" />
                  </div>
                )}
              </motion.div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-white text-blue-600 shadow-lg flex items-center justify-center"
              >
                <Camera size={20} />
              </motion.button>
            </div>

            {/* User Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
                <h1 className="text-3xl font-bold">{profileData.fullName}</h1>
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r ${getSubscriptionColor()} shadow-lg`}
                >
                  <Crown size={16} />
                  <span className="font-semibold">{subscription.plan} Plan</span>
                  {subscription.discount > 0 && <span className="text-sm">- {subscription.discount}% OFF</span>}
                </motion.div>
              </div>

              <div className="flex flex-wrap justify-center md:justify-start gap-4 text-white/80">
                <div className="flex items-center gap-2">
                  <Mail size={16} />
                  <span>{profileData.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} />
                  <span>+91 {profileData.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <span>Member since {memberSince}</span>
                </div>
              </div>
            </div>

            {/* Wallet Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/10 backdrop-blur-lg rounded-2xl p-5 min-w-[200px]"
            >
              <div className="flex items-center gap-2 mb-2">
                <Wallet size={20} className="text-white/80" />
                <span className="text-white/80 text-sm">Wallet Balance</span>
              </div>
              <p className="text-3xl font-bold">₹{walletBalance.toFixed(2)}</p>
            </motion.div>
          </div>

          {/* Action Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setEditSection("header")}
            className="absolute top-6 right-6 md:top-8 md:right-8 px-5 py-2.5 bg-white/20 backdrop-blur rounded-xl text-white font-medium hover:bg-white/30 transition-all flex items-center gap-2"
          >
            <Edit size={18} />
            Edit Profile
          </motion.button>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={TrendingUp} label="Total Orders" value="12" color="from-blue-500 to-cyan-500" delay={0} />
          <StatCard icon={Star} label="Rating" value="4.8 ★" color="from-yellow-500 to-orange-500" delay={0.1} />
          <StatCard icon={Award} label="Products" value="32" color="from-purple-500 to-pink-500" delay={0.2} />
          <StatCard icon={CheckCircle} label="Completed" value="28" color="from-green-500 to-emerald-500" delay={0.3} />
        </div>

        {/* Accordion Sections */}
        <div className="space-y-4">
          <AccordionSection
            title="Business Information"
            icon={Building}
            isOpen={accordionOpen.business}
            onToggle={() => toggleAccordion("business")}
            color="from-blue-500 to-cyan-500"
          >
            <InfoRow icon={Briefcase} label="Business Name" value={profileData.businessName} color="blue" />
            <InfoRow icon={Users} label="Business Type" value={profileData.businessType} color="green" />
            <InfoRow icon={Calendar} label="Year Established" value={profileData.yearEstablished} color="purple" />
            <InfoRow icon={Globe} label="Website" value={profileData.website} color="blue" />
            <InfoRow icon={Users} label="Employees" value={profileData.employeeCount} color="green" />
            <button
              onClick={() => setEditSection("business")}
              className="w-full mt-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <Edit size={16} />
              Edit Business Info
            </button>
          </AccordionSection>

          <AccordionSection
            title="Documents"
            icon={FileText}
            isOpen={accordionOpen.documents}
            onToggle={() => toggleAccordion("documents")}
            color="from-purple-500 to-pink-500"
          >
            <InfoRow icon={CreditCard} label="GST Number" value={profileData.gstNumber} color="purple" />
            <InfoRow icon={CreditCard} label="PAN Number" value={profileData.panNumber} color="purple" />
            <InfoRow icon={FileText} label="Aadhar" value={profileData.aadharURL ? "Uploaded" : "Not Provided"} color="green" />
            <InfoRow icon={FileText} label="PAN Document" value={profileData.panurl ? "Uploaded" : "Not Provided"} color="green" />
            <InfoRow icon={Shield} label="GST Document" value={profileData.gstDocURL ? "Uploaded" : "Not Provided"} color="green" />
            <button
              onClick={() => setEditSection("documents")}
              className="w-full mt-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <Edit size={16} />
              Edit Documents
            </button>
          </AccordionSection>

          <AccordionSection
            title="Additional Details"
            icon={Plus}
            isOpen={accordionOpen.extra}
            onToggle={() => toggleAccordion("extra")}
            color="from-orange-500 to-red-500"
          >
            <InfoRow icon={Briefcase} label="Categories" value={profileData.categories} color="orange" />
            <InfoRow icon={List} label="Sub Categories" value={profileData.subCategories} color="orange" />
            <InfoRow icon={FileText} label="Description" value={profileData.description} color="orange" />
            <button
              onClick={() => setEditSection("extra")}
              className="w-full mt-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <Edit size={16} />
              Edit Details
            </button>
          </AccordionSection>
        </div>

        {/* Edit Modal */}
        <EditModal
          isOpen={!!editSection}
          onClose={() => setEditSection(null)}
          title="Edit"
          onSave={(updated) => setProfileData({ ...profileData, ...updated })}
          prefill={profileData}
        />
      </div>
    </div>
  );
};

// Helper Icon Component
function List({ size = 18, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

export default ProfilePage;
